import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  ArrowUpRight,
  Phone,
  ShieldCheck,
  Hammer,
  Clock3,
  CheckCircle2,
  MapPin,
} from "lucide-react";
import {
  BUSINESS,
  HERO_BANNERS,
  SERVICES,
  STATS,
  TESTIMONIALS,
  GALLERY_IMAGES,
  SERVICE_AREAS,
} from "../lib/content";

/* --------------- Hero Slideshow --------------- */
const Hero = () => {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => {
      setIdx((i) => (i + 1) % HERO_BANNERS.length);
    }, 6000);
    return () => clearInterval(t);
  }, []);

  const active = HERO_BANNERS[idx];

  return (
    <section
      className="relative w-full h-screen min-h-[640px] max-h-[900px] overflow-hidden"
      data-testid="home-hero"
    >
      {/* Image layer */}
      {HERO_BANNERS.map((b, i) => (
        <div
          key={i}
          className={`absolute inset-0 transition-opacity duration-[1200ms] ease-out ${
            i === idx ? "opacity-100" : "opacity-0"
          }`}
        >
          <img
            src={b.src}
            alt={b.title}
            className={`w-full h-full object-cover ${
              i === idx ? "animate-slow-zoom" : ""
            }`}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-[#09090b]/90 via-[#09090b]/60 to-[#09090b]/20" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#09090b] via-transparent to-transparent" />
        </div>
      ))}

      {/* Content */}
      <div className="relative h-full max-w-[1400px] mx-auto px-5 md:px-10 flex flex-col justify-center pt-20">
        <div key={idx} className="max-w-4xl">
          <div className="label-sm text-[#f59e0b] mb-6 animate-fade-up">
            {active.eyebrow}
          </div>
          <h1 className="font-display font-black text-6xl sm:text-7xl md:text-[8.5rem] uppercase leading-[0.82] text-[#fafaf9] animate-fade-up">
            {active.title.split(" ").slice(0, -1).join(" ")}{" "}
            <span className="text-[#f59e0b]">
              {active.title.split(" ").slice(-1)[0]}
            </span>
          </h1>
          <p className="mt-8 text-lg md:text-2xl text-[#d4d4d8] max-w-2xl leading-snug font-light animate-fade-up">
            {active.subtitle}
          </p>

          <div className="mt-12 flex flex-col sm:flex-row gap-4 animate-fade-up">
            <a
              href={`tel:${BUSINESS.phoneRaw}`}
              className="inline-flex items-center gap-3 bg-[#f59e0b] hover:bg-[#d97706] text-[#09090b] font-bold px-8 py-5 text-sm uppercase tracking-[0.25em] transition-colors"
              data-testid="hero-cta-call"
            >
              <Phone className="w-4 h-4" strokeWidth={3} />
              {BUSINESS.phone}
            </a>
            <Link
              to="/services"
              className="inline-flex items-center gap-3 border border-[#3f3f46] hover:border-[#f59e0b] hover:bg-[#18181b] text-[#fafaf9] font-bold px-8 py-5 text-sm uppercase tracking-[0.25em] transition-colors"
              data-testid="hero-cta-services"
            >
              Our Services
              <ArrowUpRight className="w-4 h-4" strokeWidth={3} />
            </Link>
          </div>
        </div>

        {/* Slide controls */}
        <div className="absolute bottom-10 left-5 md:left-10 right-5 md:right-10 flex items-end justify-between gap-6">
          <div className="flex items-center gap-4">
            {HERO_BANNERS.map((_, i) => (
              <button
                key={i}
                onClick={() => setIdx(i)}
                data-testid={`hero-dot-${i}`}
                className={`h-[3px] transition-all duration-500 ${
                  i === idx
                    ? "bg-[#f59e0b] w-16"
                    : "bg-[#52525b] w-8 hover:bg-[#a1a1aa]"
                }`}
                aria-label={`Slide ${i + 1}`}
              />
            ))}
            <div className="ml-3 font-display text-2xl text-[#fafaf9]">
              <span className="text-[#f59e0b]">
                {String(idx + 1).padStart(2, "0")}
              </span>
              <span className="text-[#52525b]">
                {" "}
                / {String(HERO_BANNERS.length).padStart(2, "0")}
              </span>
            </div>
          </div>

          <div className="hidden md:flex items-center gap-6 text-xs uppercase tracking-[0.3em] text-[#a1a1aa]">
            <span>
              Est. <span className="text-[#fafaf9]">{BUSINESS.established}</span>
            </span>
            <span>·</span>
            <span>
              <span className="text-[#fafaf9]">{BUSINESS.yearsExperience}+</span> Years
            </span>
            <span>·</span>
            <span>Family Owned</span>
          </div>
        </div>
      </div>
    </section>
  );
};

/* --------------- Marquee strip --------------- */
const Marquee = () => {
  const items = [
    "Hot Asphalt Paving",
    "Seal Coating",
    "Crack Sealing",
    "Tar & Chip",
    "Asphalt Repairs",
    "Driveways",
    "Parking Lots",
    "Commercial",
    "Residential",
  ];
  return (
    <div
      className="border-y border-[#27272a] bg-[#09090b] overflow-hidden py-6"
      data-testid="home-marquee"
    >
      <div className="flex animate-marquee whitespace-nowrap">
        {[...items, ...items, ...items].map((t, i) => (
          <div
            key={i}
            className="flex items-center gap-10 px-6 font-display text-3xl md:text-4xl uppercase font-bold"
          >
            <span className="text-[#fafaf9]">{t}</span>
            <span className="text-[#f59e0b] text-xl">✦</span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* --------------- About Teaser --------------- */
const AboutTeaser = () => {
  return (
    <section className="relative py-24 md:py-40" data-testid="home-about-teaser">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
        <div className="lg:col-span-5 relative">
          <div className="aspect-[4/5] relative overflow-hidden border border-[#27272a]">
            <img
              src="/images/IMG_12.jpg"
              alt="North West Asphalt crew at work"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#09090b]/70 to-transparent" />
          </div>
          <div className="absolute -bottom-6 -right-6 bg-[#f59e0b] text-[#09090b] p-6 md:p-8 max-w-[260px]">
            <div className="font-display font-black text-6xl md:text-7xl leading-none">
              40+
            </div>
            <div className="text-xs uppercase tracking-[0.25em] font-bold mt-2">
              Years of proven work in Western Canada
            </div>
          </div>
        </div>
        <div className="lg:col-span-7 flex flex-col justify-center">
          <div className="label-sm text-[#f59e0b] mb-6">About the company</div>
          <h2 className="font-display font-black text-4xl sm:text-5xl md:text-6xl uppercase leading-[0.9] mb-8">
            Family owned
            <br />
            <span className="text-[#f59e0b]">since 1984.</span>
          </h2>
          <p className="text-[#d4d4d8] text-lg leading-relaxed mb-6 max-w-2xl">
            North West Asphalt has been one of Western Canada's most prestigious
            providers of asphalt paving services for over four decades. We deliver
            reliable paving, repair, and maintenance solutions for commercial,
            industrial and residential clients — built to last a lifetime.
          </p>
          <p className="text-[#a1a1aa] text-base leading-relaxed mb-10 max-w-2xl">
            Every job starts below the surface. Proper base prep, drainage
            correction, and hot-mix asphalt installed at the right thickness —
            that's how we deliver work that holds up through decades of real use.
          </p>

          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to="/about"
              className="inline-flex items-center gap-3 bg-[#f59e0b] hover:bg-[#d97706] text-[#09090b] font-bold px-8 py-5 text-sm uppercase tracking-[0.25em] transition-colors"
              data-testid="home-about-cta"
            >
              Our Story
              <ArrowUpRight className="w-4 h-4" strokeWidth={3} />
            </Link>
            <a
              href={`tel:${BUSINESS.phoneRaw}`}
              className="inline-flex items-center gap-3 border border-[#3f3f46] hover:border-[#f59e0b] text-[#fafaf9] font-bold px-8 py-5 text-sm uppercase tracking-[0.25em] transition-colors"
              data-testid="home-about-call"
            >
              <Phone className="w-4 h-4" strokeWidth={3} />
              Talk to us
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

/* --------------- Services Grid --------------- */
const ServicesGrid = () => {
  return (
    <section
      className="relative py-24 md:py-32 bg-[#050505] border-y border-[#27272a]"
      data-testid="home-services"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-6 mb-16">
          <div>
            <div className="label-sm text-[#f59e0b] mb-5">What we do</div>
            <h2 className="font-display font-black text-4xl sm:text-5xl md:text-6xl uppercase leading-[0.9]">
              Five disciplines.
              <br />
              <span className="text-[#f59e0b]">One standard.</span>
            </h2>
          </div>
          <p className="text-[#a1a1aa] max-w-md">
            From hot asphalt paving to rubberized crack sealing — every service
            we offer is engineered for the freeze-thaw cycles of the North.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {SERVICES.map((s, i) => (
            <Link
              to="/services"
              key={s.slug}
              data-testid={`home-service-card-${s.slug}`}
              className={`group relative bg-[#18181b] border border-[#27272a] hover:border-[#f59e0b] transition-all duration-500 overflow-hidden ${
                i === 0 ? "md:col-span-2 lg:row-span-2" : ""
              }`}
            >
              <div className={`${i === 0 ? "aspect-[16/10] lg:aspect-auto lg:h-full" : "aspect-[4/3]"} relative overflow-hidden`}>
                <img
                  src={s.image}
                  alt={s.title}
                  className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-110"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#09090b] via-[#09090b]/40 to-transparent" />
                <div className="absolute top-5 left-5 w-10 h-10 border border-[#f59e0b] text-[#f59e0b] flex items-center justify-center font-display font-black text-sm">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                  <h3 className="font-display font-black text-2xl md:text-3xl uppercase leading-tight mb-3 group-hover:text-[#f59e0b] transition-colors">
                    {s.title}
                  </h3>
                  <p className="text-[#d4d4d8] text-sm leading-relaxed max-w-md mb-5">
                    {s.short}
                  </p>
                  <div className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.25em] font-bold text-[#f59e0b]">
                    Learn more
                    <ArrowUpRight
                      className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
                      strokeWidth={3}
                    />
                  </div>
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
};

/* --------------- Stats Band --------------- */
const StatsBand = () => {
  return (
    <section
      className="relative py-20 md:py-24 bg-[#09090b] border-b border-[#27272a]"
      data-testid="home-stats"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10 grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-0 md:divide-x md:divide-[#27272a]">
        {STATS.map((s, i) => (
          <div key={i} className="md:px-10 first:md:pl-0 last:md:pr-0">
            <div className="font-display font-black text-6xl md:text-7xl text-[#f59e0b] leading-none mb-4">
              {s.value}
            </div>
            <div className="text-xs uppercase tracking-[0.25em] text-[#a1a1aa]">
              {s.label}
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

/* --------------- Why Choose Us --------------- */
const WhyUs = () => {
  const items = [
    {
      num: "01",
      icon: Clock3,
      title: "Over 40 Years",
      tag: "Experience",
      body:
        "Four decades of expertise serving communities across Western Canada with trusted paving solutions.",
    },
    {
      num: "02",
      icon: ShieldCheck,
      title: "Built To Last",
      tag: "Engineering",
      body:
        "Proper base prep, drainage correction, and premium hot-mix asphalt at the right thickness.",
    },
    {
      num: "03",
      icon: Hammer,
      title: "Family Owned",
      tag: "Since 1984",
      body:
        "We still treat every driveway, lot and road like it belongs to us. Three generations of craftsmanship.",
    },
    {
      num: "04",
      icon: CheckCircle2,
      title: "Honest Pricing",
      tag: "Transparent",
      body:
        "Clear written quotes. No surprises. No shortcuts. Transparent from first call to final inspection.",
    },
  ];
  return (
    <section className="relative py-24 md:py-32" data-testid="home-why-us">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-16 items-end">
          <div className="lg:col-span-7">
            <div className="label-sm text-[#f59e0b] mb-5">Why Choose Us</div>
            <h2 className="font-display font-black text-4xl sm:text-5xl md:text-6xl uppercase leading-[0.9]">
              Built on trust.
              <br />
              <span className="text-[#f59e0b]">Paved with pride.</span>
            </h2>
          </div>
          <p className="lg:col-span-5 text-[#a1a1aa] leading-relaxed">
            Founded in 1984, North West Asphalt has established itself as one
            of the most prestigious providers of asphalt paving services in
            Western Canada — trusted by homeowners, businesses and municipalities.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5">
          {items.map((item, i) => (
            <div
              key={i}
              className="group relative bg-[#18181b] border border-[#27272a] hover:border-[#f59e0b] transition-all duration-500 overflow-hidden flex flex-col"
              data-testid={`why-us-item-${i}`}
            >
              {/* Accent strip that slides */}
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-[#f59e0b] scale-y-0 origin-top group-hover:scale-y-100 transition-transform duration-500" />

              {/* Header row */}
              <div className="flex items-start justify-between p-7 pb-4">
                <div className="font-display font-black text-5xl text-[#27272a] leading-none group-hover:text-[#f59e0b] transition-colors">
                  {item.num}
                </div>
                <div className="w-12 h-12 bg-[#09090b] border border-[#27272a] flex items-center justify-center text-[#f59e0b] group-hover:bg-[#f59e0b] group-hover:text-[#09090b] group-hover:border-[#f59e0b] transition-all">
                  <item.icon className="w-5 h-5" strokeWidth={2.25} />
                </div>
              </div>

              {/* Divider */}
              <div className="mx-7 h-px bg-[#27272a] group-hover:bg-[#f59e0b]/40 transition-colors" />

              {/* Body */}
              <div className="px-7 py-6 flex-1 flex flex-col">
                <div className="text-[10px] uppercase tracking-[0.3em] text-[#f59e0b] font-bold mb-3">
                  {item.tag}
                </div>
                <h3 className="font-display font-black text-2xl md:text-[1.75rem] uppercase leading-tight mb-4">
                  {item.title}
                </h3>
                <p className="text-[#a1a1aa] text-sm leading-relaxed flex-1">
                  {item.body}
                </p>
              </div>

              {/* Footer learn more */}
              <div className="px-7 py-5 border-t border-[#27272a] group-hover:border-[#f59e0b]/30 transition-colors flex items-center justify-between text-[10px] uppercase tracking-[0.3em] text-[#52525b] group-hover:text-[#f59e0b]">
                <span>The NW Standard</span>
                <ArrowUpRight className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1" strokeWidth={2.5} />
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* --------------- Project Strip --------------- */
const ProjectStrip = () => {
  const preview = GALLERY_IMAGES.slice(0, 8);
  return (
    <section
      className="relative py-24 md:py-32 bg-[#050505] border-y border-[#27272a]"
      data-testid="home-projects"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="flex flex-col md:flex-row items-start md:items-end justify-between gap-6 mb-14">
          <div>
            <div className="label-sm text-[#f59e0b] mb-5">Recent projects</div>
            <h2 className="font-display font-black text-4xl sm:text-5xl md:text-6xl uppercase leading-[0.9]">
              Proof in the pavement.
            </h2>
          </div>
          <Link
            to="/gallery"
            data-testid="home-gallery-link"
            className="inline-flex items-center gap-3 border border-[#3f3f46] hover:border-[#f59e0b] text-[#fafaf9] font-bold px-6 py-4 text-xs uppercase tracking-[0.25em] transition-colors"
          >
            View Gallery
            <ArrowUpRight className="w-4 h-4" strokeWidth={3} />
          </Link>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {preview.map((img, i) => (
            <div
              key={i}
              className="group relative overflow-hidden border border-[#27272a] aspect-square hover:border-[#f59e0b]/60 transition-colors"
              data-testid={`home-project-${i}`}
            >
              <img
                src={img.src}
                alt={img.alt}
                loading="lazy"
                className="w-full h-full object-cover transition-transform duration-[1200ms] group-hover:scale-110"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#09090b]/80 via-transparent to-transparent opacity-60 group-hover:opacity-100 transition-opacity" />
              <div className="absolute top-3 left-3 w-8 h-8 border border-[#f59e0b]/60 bg-[#09090b]/60 backdrop-blur text-[#f59e0b] flex items-center justify-center font-display font-black text-[11px]">
                {String(i + 1).padStart(2, "0")}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* --------------- Service Areas --------------- */
const ServiceAreaTeaser = () => {
  const meta = {
    Edmonton: {
      sub: "Alberta Capital",
      image:
        "https://images.unsplash.com/photo-1772852340517-b11ec88979fc?fm=jpg&q=80&w=1400&auto=format&fit=crop",
      tags: ["Residential", "Commercial", "Municipal"],
    },
    "Grand Prairie": {
      sub: "Peace Country",
      image:
        "https://images.unsplash.com/photo-1708117242652-25dc76c4b30c?fm=jpg&q=80&w=1400&auto=format&fit=crop",
      tags: ["Rural", "Industrial", "Acreages"],
    },
    Lloydminster: {
      sub: "Border City · AB/SK",
      image:
        "https://images.unsplash.com/photo-1776381733574-372689e246b4?fm=jpg&q=80&w=1400&auto=format&fit=crop",
      tags: ["Oil Sector", "Commercial", "Residential"],
    },
  };

  return (
    <section
      className="relative py-24 md:py-32 border-b border-[#27272a]"
      data-testid="home-service-areas"
    >
      {/* soft amber glow */}
      <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-[#f59e0b]/5 blur-3xl pointer-events-none" />

      <div className="relative max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-14 items-end">
          <div className="lg:col-span-7">
            <div className="label-sm text-[#f59e0b] mb-5">Service area</div>
            <h2 className="font-display font-black text-4xl sm:text-5xl md:text-6xl uppercase leading-[0.9]">
              Serving the
              <br />
              <span className="text-[#f59e0b]">entire North.</span>
            </h2>
          </div>
          <div className="lg:col-span-5 space-y-5">
            <p className="text-[#a1a1aa] text-base md:text-lg leading-relaxed">
              Three regional hubs covering Western Canada. Residential,
              commercial, industrial and municipal — we travel beyond for
              larger projects.
            </p>
            <Link
              to="/service-area"
              data-testid="home-areas-link"
              className="inline-flex items-center gap-3 border border-[#3f3f46] hover:border-[#f59e0b] text-[#fafaf9] font-bold px-6 py-4 text-xs uppercase tracking-[0.25em] transition-colors"
            >
              Full coverage map
              <ArrowUpRight className="w-4 h-4" strokeWidth={3} />
            </Link>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {SERVICE_AREAS.map((a, i) => {
            const m = meta[a.city] || {};
            return (
              <div
                key={i}
                className="group relative bg-[#18181b] border border-[#27272a] hover:border-[#f59e0b] transition-all duration-500 overflow-hidden flex flex-col"
                data-testid={`home-area-${i}`}
              >
                {/* Image */}
                <div className="relative aspect-[16/10] overflow-hidden">
                  <img
                    src={m.image}
                    alt={a.city}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-[1400ms] group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#09090b] via-[#09090b]/40 to-transparent" />
                  <div className="absolute top-4 left-4 w-10 h-10 bg-[#f59e0b] text-[#09090b] flex items-center justify-center font-display font-black text-sm">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="absolute top-4 right-4 flex items-center gap-2 bg-[#09090b]/70 backdrop-blur px-3 py-1.5 border border-[#27272a]">
                    <MapPin
                      className="w-3.5 h-3.5 text-[#f59e0b]"
                      strokeWidth={2.5}
                    />
                    <span className="text-[10px] uppercase tracking-[0.25em] text-[#fafaf9] font-bold">
                      {a.region.split(" /")[0]}
                    </span>
                  </div>
                  <div className="absolute bottom-4 left-5 right-5">
                    <div className="text-[10px] uppercase tracking-[0.3em] text-[#f59e0b] font-bold mb-1.5">
                      {m.sub}
                    </div>
                    <div className="font-display font-black text-3xl md:text-4xl uppercase leading-none text-[#fafaf9]">
                      {a.city}
                    </div>
                  </div>
                </div>

                {/* Body */}
                <div className="p-6 flex-1 flex flex-col">
                  <p className="text-[#a1a1aa] text-sm leading-relaxed mb-5">
                    {a.note}
                  </p>

                  {/* Coverage tags */}
                  <div className="flex flex-wrap gap-2 mb-5">
                    {m.tags?.map((t, j) => (
                      <span
                        key={j}
                        className="text-[10px] uppercase tracking-[0.2em] text-[#a1a1aa] border border-[#27272a] px-2.5 py-1.5 font-bold group-hover:border-[#f59e0b]/40 group-hover:text-[#f59e0b] transition-colors"
                      >
                        {t}
                      </span>
                    ))}
                  </div>

                  {/* Footer link */}
                  <Link
                    to="/service-area"
                    data-testid={`home-area-${i}-link`}
                    className="mt-auto flex items-center justify-between pt-4 border-t border-[#27272a] group-hover:border-[#f59e0b]/30 text-[10px] uppercase tracking-[0.3em] font-bold text-[#52525b] group-hover:text-[#f59e0b] transition-colors"
                  >
                    <span>View region</span>
                    <ArrowUpRight
                      className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
                      strokeWidth={2.5}
                    />
                  </Link>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

/* --------------- Testimonials --------------- */
const Testimonials = () => {
  return (
    <section
      className="relative py-24 md:py-32 bg-[#050505] border-y border-[#27272a]"
      data-testid="home-testimonials"
    >
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="label-sm text-[#f59e0b] mb-5">Words from clients</div>
        <h2 className="font-display font-black text-4xl sm:text-5xl md:text-6xl uppercase leading-[0.9] mb-16 max-w-4xl">
          Driveways that last.
          <br />
          <span className="text-[#f59e0b]">Reputations earned.</span>
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {TESTIMONIALS.map((t, i) => (
            <div
              key={i}
              className="bg-[#18181b] border border-[#27272a] p-8 md:p-10 flex flex-col"
              data-testid={`testimonial-${i}`}
            >
              <div className="text-[#f59e0b] text-2xl font-display font-black mb-4">
                ★★★★★
              </div>
              <p className="text-[#fafaf9] text-lg leading-relaxed flex-1">
                "{t.quote}"
              </p>
              <div className="mt-8 pt-6 border-t border-[#27272a]">
                <div className="font-bold text-sm">{t.name}</div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#a1a1aa] mt-1">
                  {t.location}
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

/* --------------- Final CTA --------------- */
const FinalCTA = () => {
  return (
    <section
      className="relative overflow-hidden"
      data-testid="home-final-cta"
    >
      {/* Background: dark asphalt image with gradient */}
      <div className="absolute inset-0">
        <img
          src="/images/IMG_6.jpg"
          alt=""
          aria-hidden
          className="w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#f59e0b] via-[#f59e0b] to-[#d97706]" />
        <div className="absolute inset-0 mix-blend-multiply opacity-30 bg-[linear-gradient(135deg,transparent_0%,#000_100%)]" />
      </div>

      {/* Decorative corner marks (desktop only) */}
      <div className="hidden md:block absolute top-8 left-8 w-10 h-10 border-t-[3px] border-l-[3px] border-[#09090b]/30 pointer-events-none" />
      <div className="hidden md:block absolute top-8 right-8 w-10 h-10 border-t-[3px] border-r-[3px] border-[#09090b]/30 pointer-events-none" />
      <div className="hidden md:block absolute bottom-8 left-8 w-10 h-10 border-b-[3px] border-l-[3px] border-[#09090b]/30 pointer-events-none" />
      <div className="hidden md:block absolute bottom-8 right-8 w-10 h-10 border-b-[3px] border-r-[3px] border-[#09090b]/30 pointer-events-none" />

      <div className="relative max-w-[1400px] mx-auto px-5 md:px-10 py-20 md:py-32">
        <div className="max-w-4xl mx-auto text-center">
          {/* Eyebrow pill */}
          <div
            className="inline-flex items-center gap-2 bg-[#09090b] text-[#f59e0b] px-4 py-2 mb-8 text-[10px] uppercase tracking-[0.35em] font-bold"
            data-testid="final-cta-pill"
          >
            <span className="w-2 h-2 bg-[#f59e0b] rounded-full animate-pulse" />
            Ready when you are
          </div>

          {/* Headline — controlled responsive scale */}
          <h2 className="font-display font-black uppercase leading-[0.88] text-[#09090b] mb-6 text-[2.75rem] sm:text-6xl md:text-[6.5rem] tracking-tight">
            Let's pave
            <br className="hidden sm:block" />{" "}
            <span className="inline-block">something</span>
            <br />
            <span className="inline-block bg-[#09090b] text-[#f59e0b] px-3 sm:px-5 leading-[0.95]">
              that lasts.
            </span>
          </h2>

          {/* Sub */}
          <p className="text-[#09090b]/80 text-base md:text-xl max-w-xl mx-auto mb-10 font-medium leading-relaxed">
            Family owned since 1984. Honest quotes, disciplined prep, and
            hot-mix asphalt built for the North.
          </p>

          {/* CTA Buttons — full width on mobile, inline on desktop */}
          <div className="flex flex-col sm:flex-row justify-center gap-3 sm:gap-4 max-w-md sm:max-w-none mx-auto">
            <a
              href={`tel:${BUSINESS.phoneRaw}`}
              className="group flex items-center justify-center gap-3 bg-[#09090b] hover:bg-[#18181b] text-[#fafaf9] font-bold px-6 sm:px-8 py-5 text-xs sm:text-sm uppercase tracking-[0.22em] transition-colors"
              data-testid="home-final-call"
            >
              <Phone className="w-4 h-4 group-hover:rotate-12 transition-transform" strokeWidth={3} />
              <span>{BUSINESS.phone}</span>
            </a>
            <Link
              to="/contact"
              className="group flex items-center justify-center gap-3 bg-transparent border-2 border-[#09090b] hover:bg-[#09090b] hover:text-[#fafaf9] text-[#09090b] font-bold px-6 sm:px-8 py-5 text-xs sm:text-sm uppercase tracking-[0.22em] transition-colors"
              data-testid="home-final-contact"
            >
              <span>Get In Touch</span>
              <ArrowUpRight
                className="w-4 h-4 transition-transform group-hover:translate-x-1 group-hover:-translate-y-1"
                strokeWidth={3}
              />
            </Link>
          </div>

          {/* Micro meta row */}
          <div className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-[10px] uppercase tracking-[0.3em] text-[#09090b]/70 font-bold">
            <span className="flex items-center gap-2">
              <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={3} />
              Family owned · 1984
            </span>
            <span className="hidden sm:inline">·</span>
            <span className="flex items-center gap-2">
              <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={3} />
              Free site visits
            </span>
            <span className="hidden sm:inline">·</span>
            <span className="flex items-center gap-2">
              <CheckCircle2 className="w-3.5 h-3.5" strokeWidth={3} />
              Honest quotes
            </span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default function Home() {
  return (
    <div data-testid="home-page">
      <Hero />
      <Marquee />
      <AboutTeaser />
      <ServicesGrid />
      <StatsBand />
      <WhyUs />
      <ProjectStrip />
      <ServiceAreaTeaser />
      <Testimonials />
      <FinalCTA />
    </div>
  );
}
